/**
 * Einheitlicher Druck-/Export-Einstieg für UI und Services.
 */

import printService from './printService';
import centralizedExportPrintService from './centralizedExportPrintService';
import {
  getSavedPrinter,
  getSavedPrinterDisplayName,
  hasSavedPrinter,
  resolvePrinterDeviceName,
  warmSilentPrintPipeline,
  probePhysicalPrinterConnected,
  printSilentFast,
  printSilentToSavedPrinter,
  showPrinterPicker,
  printWithPreviewOrSilent,
  printPrinterRoleTest,
  invalidateResolvedPrinterCache,
  resolveRoleTargetDeviceName,
  resolveRoleStrictRecoveryDevice
} from '../utils/printerUtils';
import {
  PRINT_ROLE,
  PRINT_ROLE_IDS,
  PRINT_ROLE_META,
  getPrinterRoleAssignments,
  getPrinterRoleAssignment,
  setPrinterRoleAssignment,
  clearPrinterRoleAssignment,
  assessAllPrinterRolesHealth,
  assessPrinterRoleHealth,
  summarizePrinterRolesHealth,
  resolveEffectivePrinterTarget,
  resolvePrinterRoleTestPlan,
  printKindToRole,
  roleToPrintKind,
  PRINTER_ROLES_CHANGED_EVENT,
  subscribePrinterRolesChanged,
  notifyPrinterRolesChanged
} from '../domain/print';
import {
  printInvoice,
  printInvoiceDocument,
  buildInvoicePrintHtml,
  resolveInvoicePrintFormat,
  loadBusinessInfo
} from '../utils/invoicePrintUtils';
import { printHtmlDocument } from '../utils/printHtmlDocument';
import { receiptCalculationService } from './receiptCalculationService';

export const printFacade = {
  // POS / silent receipt
  printSilent: printSilentFast,
  printSilentToSavedPrinter,
  resolvePrinter: resolvePrinterDeviceName,
  warmSilentPipeline: warmSilentPrintPipeline,
  probePhysicalPrinter: probePhysicalPrinterConnected,
  showPrinterPicker,
  getSavedPrinter,
  getSavedPrinterDisplayName,
  hasSavedPrinter,
  printWithPreviewOrSilent,
  invalidateResolvedPrinterCache,

  // Role-based setup (receipt / document / label)
  PRINT_ROLE,
  PRINT_ROLE_IDS,
  PRINT_ROLE_META,
  getPrinterRoleAssignments,
  getPrinterRoleAssignment,
  setPrinterRoleAssignment,
  clearPrinterRoleAssignment,
  assessAllPrinterRolesHealth,
  assessPrinterRoleHealth,
  summarizePrinterRolesHealth,
  resolveEffectivePrinterTarget,
  resolvePrinterRoleTestPlan,
  printKindToRole,
  roleToPrintKind,
  PRINTER_ROLES_CHANGED_EVENT,
  subscribePrinterRolesChanged,
  notifyPrinterRolesChanged,
  printRoleTest: printPrinterRoleTest,
  resolveRoleTargetDeviceName,
  resolveRoleStrictRecoveryDevice,

  // Invoices
  printInvoice,
  printInvoiceDocument,
  buildInvoicePrintHtml,
  resolveInvoicePrintFormat,
  loadBusinessInfo,

  // Generic HTML
  printHtml: printHtmlDocument,

  // Receipts (thermal POS)
  printReceipt: (saleData, options) => receiptCalculationService.printReceipt(saleData, options),
  buildClientReceiptHtml: (saleData, options) =>
    receiptCalculationService.buildClientReceiptHTML(saleData, options),
  buildMerchantReceiptHtml: (saleData, options) =>
    receiptCalculationService.buildMerchantReceiptHTML(saleData, options),

  // Reports / exports
  getBilingualLabels: (key) => centralizedExportPrintService.getBilingualLabels(key),
  printBilingual: (data, type, options) =>
    centralizedExportPrintService.printBilingual(data, type, options),
  exportReport: (data, format, options) =>
    centralizedExportPrintService.exportBilingual(data, format, options),
  buildReportHtml: (data, options) =>
    centralizedExportPrintService.buildReportHtml(data, options),
  exportBilingual: (data, format, options) =>
    centralizedExportPrintService.exportBilingual(data, format, options),

  // Low-level Electron print service
  listPrinters: () => printService.listPrinters(),
  openPreview: (html, options) => printService.openPreview(html, options),
  generatePDF: (html, options) => printService.generatePDF(html, options),
  exportToPDF: (data, options) => printService.exportToPDF(data, options),
  exportToExcel: (data, options) => printService.exportToExcel(data, options),
  exportToCSV: (data, options) => printService.exportToCSV(data, options),
  isElectron: () => printService.isElectronEnvironment()
};

export default printFacade;
